import { z } from "zod";
import {
  PrintTemplateSchema,
  type PrintInventoryType,
  type PrintTemplate,
} from "./print-template.zod";

/**
 * Clés des sections et des champs imprimables par type d'inventaire : de quoi valider un modèle côté serveur sans embarquer
 * les composants d'impression. Le registre complet (libellés, résolution des valeurs) vit dans `@repo/print-pdf` ; un test
 * de ce paquet vérifie que les deux listes restent identiques. Une clé n'est jamais renommée ni réutilisée.
 */
export interface PrintTemplateKeys {
  sections: readonly string[];
  fields: Readonly<Record<string, { section: string; locked?: boolean }>>;
}

export const PRINT_TEMPLATE_KEYS: Partial<
  Record<PrintInventoryType, PrintTemplateKeys>
> = {
  ARBRE: {
    sections: [
      "identification",
      "caracteristiques",
      "environnement",
      "diagnostic_visuel",
      "diagnostic_sanitaire",
      "commentaires",
    ],
    fields: {
      date: { section: "identification", locked: true },
      genre_latin: { section: "identification" },
      nom_fr: { section: "identification" },
      adresse: { section: "identification" },
      code_site: { section: "identification", locked: true },
      espece_latin: { section: "identification" },
      identification: { section: "identification", locked: true },
      cultivar: { section: "identification" },
      gps: { section: "identification" },
      hauteur: { section: "caracteristiques" },
      diametre: { section: "caracteristiques" },
      age_estime: { section: "caracteristiques" },
      stade_dev: { section: "caracteristiques" },
      remarquable: { section: "caracteristiques" },
      port_arbre: { section: "caracteristiques" },
      caract_tronc: { section: "caracteristiques" },
      caract_pied: { section: "caracteristiques" },
      implantation: { section: "caracteristiques" },
      domaine: { section: "caracteristiques" },
      situation: { section: "caracteristiques" },
      acces: { section: "caracteristiques" },
      proximite: { section: "environnement" },
      dict: { section: "environnement" },
      nuisance: { section: "environnement" },
      taux_freq: { section: "environnement" },
      type_passage: { section: "environnement" },
      bloc_diagnostic_visuel: { section: "diagnostic_visuel" },
      bloc_diagnostic_sanitaire: { section: "diagnostic_sanitaire" },
      bloc_commentaires: { section: "commentaires" },
    },
  },
  EPAYSAGE: {
    sections: [
      "identification",
      "adresse",
      "caracteristiques",
      "risque_age",
      "photos",
      "commentaire",
      "essence_identite",
      "essence_diagnostic",
      "essence_travaux",
    ],
    fields: {
      date: { section: "identification", locked: true },
      type_espace: { section: "identification" },
      nb_arbres_total: { section: "identification" },
      code_site: { section: "identification", locked: true },
      identification: { section: "identification", locked: true },
      essence_1: { section: "identification" },
      essence_2: { section: "identification" },
      essence_3: { section: "identification" },
      essence_4: { section: "identification" },
      essence_5: { section: "identification" },
      surface: { section: "identification" },
      adresse: { section: "adresse" },
      milieu: { section: "caracteristiques" },
      sol: { section: "caracteristiques" },
      nuisance: { section: "caracteristiques" },
      situation: { section: "caracteristiques" },
      domaine: { section: "caracteristiques" },
      proximite: { section: "caracteristiques" },
      accessibilite: { section: "caracteristiques" },
      taux_freq: { section: "caracteristiques" },
      type_passage: { section: "caracteristiques" },
      bloc_risque_global: { section: "risque_age" },
      age_estime: { section: "risque_age" },
      bloc_photos: { section: "photos" },
      bloc_commentaire: { section: "commentaire" },
      essence_nom: { section: "essence_identite" },
      essence_nb_arbres: { section: "essence_identite" },
      essence_diametre: { section: "essence_identite" },
      essence_hauteur: { section: "essence_identite" },
      bloc_essence_photos: { section: "essence_identite" },
      essence_stade_dev: { section: "essence_diagnostic" },
      essence_etat: { section: "essence_diagnostic" },
      essence_port: { section: "essence_diagnostic" },
      essence_critere: { section: "essence_diagnostic" },
      bloc_essence_risque: { section: "essence_diagnostic" },
      travaux_aucun: { section: "essence_travaux" },
      travaux_abattage: { section: "essence_travaux" },
      travaux_demontage: { section: "essence_travaux" },
      travaux_mise_en_securite: { section: "essence_travaux" },
      travaux_taille_sanitaire: { section: "essence_travaux" },
      travaux_couple_selective: { section: "essence_travaux" },
      travaux_dechiffrage: { section: "essence_travaux" },
      travaux_other: { section: "essence_travaux" },
      date_travaux: { section: "essence_travaux" },
      date_visite: { section: "essence_travaux" },
      travaux_com: { section: "essence_travaux" },
    },
  },
};

/** Taille maximale d'un modèle enregistré (JSON sérialisé). */
export const PRINT_TEMPLATE_MAX_BYTES = 64 * 1024;

/** Contrôles propres au registre (le schéma Zod vérifie déjà la forme) : liste d'erreurs, vide si le modèle est valide. */
export function validatePrintTemplateKeys(template: PrintTemplate): string[] {
  const registry = PRINT_TEMPLATE_KEYS[template.inventoryType];
  if (!registry)
    return [
      `Type d'inventaire sans modèle de fiche : ${template.inventoryType}`,
    ];

  const errors: string[] = [];
  const sectionIds = new Set(registry.sections);
  const seenSections = new Set<string>();
  for (const section of template.sections) {
    if (seenSections.has(section.id))
      errors.push(`Section en double : ${section.id}`);
    seenSections.add(section.id);
    if (section.kind === "builtin" && !sectionIds.has(section.id))
      errors.push(`Section inconnue : ${section.id}`);

    const seenFields = new Set<string>();
    for (const item of section.items) {
      const field = registry.fields[item.field];
      if (!field) errors.push(`Champ inconnu : ${item.field}`);
      else if (field.locked && !item.visible)
        errors.push(`Le champ « ${item.field} » ne peut pas être masqué`);
      if (seenFields.has(item.field))
        errors.push(`Champ en double dans ${section.id} : ${item.field}`);
      seenFields.add(item.field);
      if (item.col > section.columns)
        errors.push(
          `Colonne ${item.col} hors de la section ${section.id} (${section.columns} colonnes)`,
        );
    }
  }
  return errors;
}

/** Corps de `PUT /print-templates/:type` : le modèle, et la révision que l'éditeur a chargée (0 = aucun modèle enregistré). */
export const PrintTemplateSaveSchema = z.object({
  config: PrintTemplateSchema,
  expectedRevision: z.number().int().min(0),
});
export type PrintTemplateSave = z.infer<typeof PrintTemplateSaveSchema>;

/** Modèle applicable à l'organisation de l'utilisateur ; `config` est null tant que l'organisation n'en a pas enregistré. */
export interface EffectivePrintTemplate {
  config: PrintTemplate | null;
  revision: number;
  updated_at: string | null;
}
